import { useCallback, useRef } from 'react';

import useSWRSubscription, { SWRSubscriptionOptions } from 'swr/subscription';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

// Utils
import { get } from '@/utils/client';

const WS_URL = process.env.NEXT_PUBLIC_WEBSOCKET_URL;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 1000;

export const WEBSOCKET_EVENTS = {
    CONNECTION_ESTABLISHED: 'pusher:connection_established',
    ERROR: 'pusher:error',
    PING: 'pusher:ping',
    PONG: 'pusher:pong',
    SUBSCRIBE: 'pusher:subscribe',
    SUBSCRIBED: 'pusher_internal:subscription_succeeded',

    // Instagram
    INSTAGRAM_CONVERSATION_REFRESH: 'messaging.instagram.conversation.refresh',
    INSTAGRAM_INBOUND_MESSAGE: 'messaging.instagram.send',

    // Facebook
    FACEBOOK_CONVERSATION_REFRESH: 'messaging.messenger.conversation.refresh',
    FACEBOOK_INBOUND_MESSAGE: 'messaging.messenger.send'
} as const;

type UseSWRWebSocketReturn<T> = {
    readonly data: T;
    readonly error?: Error;
    readonly isConnected: boolean;
};

type ConnectionData = {
    socket_id: string;
};

export function useSWRSocket<T>(): UseSWRWebSocketReturn<T> {
    const ws = useRef<WebSocket | null>(null);
    const reconnectAttempts = useRef<number>(0);
    const isUnmounting = useRef<boolean>(false);

    const { user } = useUser();

    const handleError = (error: unknown, next: SWRSubscriptionOptions<T, Error>['next']) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown WebSocket error';
        console.error('[WebSocket]:', errorMessage);
        next(new Error(errorMessage));
    };

    const handleMessage = (event: MessageEvent, next: SWRSubscriptionOptions<T, Error>['next']) => {
        try {
            const message = JSON.parse(event.data);

            switch (message.event) {
                case WEBSOCKET_EVENTS.CONNECTION_ESTABLISHED:
                    console.info('[WebSocket]: Connection established');
                    reconnectAttempts.current = 0;
                    void handleOpenConnection(next, JSON.parse(message.data));
                    break;
                case WEBSOCKET_EVENTS.SUBSCRIBED:
                    console.info('[WebSocket]: Subscribed to channel');
                    break;
                case WEBSOCKET_EVENTS.ERROR:
                    handleError(message.data, next);
                    break;
                case WEBSOCKET_EVENTS.PING:
                    ws.current?.send(JSON.stringify({ event: WEBSOCKET_EVENTS.PONG, data: {} }));
                    break;
                default:
                    next(null, {
                        event: message.event,
                        data: JSON.parse(message.data),
                        timestamp: Date.now()
                    } as T);
            }
        } catch (err) {
            handleError(err, next);
        }
    };

    /**
     * Fetches authentication token for WebSocket connection
     * @throws {Error} If token fetch fails
     */
    async function getAuthToken({ socket_id }: ConnectionData): Promise<string> {
        const response = await get<{ data: { auth: string } }>('/broadcasting/auth', {
            socket_id,
            channel_name: `private-user.${user.id}`
        });

        if (!response?.data?.auth) {
            console.error('[WebSocket]: Invalid authentication response', response);
            throw new Error('Invalid authentication response');
        }

        return response?.data?.auth;
    }

    const handleOpenConnection = async (
        next: SWRSubscriptionOptions<T, Error>['next'],
        connectionData: ConnectionData
    ) => {
        try {
            const auth = await getAuthToken(connectionData);

            const subscribePayload = {
                event: WEBSOCKET_EVENTS.SUBSCRIBE,
                data: {
                    channel: `private-user.${user.id}`,
                    auth
                }
            };

            if (ws.current?.readyState === WebSocket.OPEN) {
                ws.current.send(JSON.stringify(subscribePayload));
            }
        } catch (err) {
            handleError(err, next);
        }
    };

    const attemptReconnect = useCallback((connect: () => void) => {
        if (isUnmounting.current) return;

        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttempts.current += 1;
            console.warn(
                `[WebSocket]: Reconnecting... Attempt ${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS}`
            );
            setTimeout(connect, RECONNECT_DELAY);
        } else {
            console.error(
                `[WebSocket]: Maximum reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Giving up.`
            );
        }
    }, []);

    const { data, error } = useSWRSubscription(
        user.id ? `${WS_URL}` : null,
        (key, { next }: SWRSubscriptionOptions<T, Error>) => {
            const connect = () => {
                if (!key || isUnmounting.current) return;

                // Clean up any existing connection
                if (ws.current) {
                    ws.current.close();
                    ws.current = null;
                }

                ws.current = new WebSocket(key);

                ws.current.onopen = () => {
                    console.info('[WebSocket]: Connection opened');
                };

                ws.current.onmessage = (event) => handleMessage(event, next);

                ws.current.onerror = (err) => handleError(err, next);

                ws.current.onclose = () => {
                    console.warn('[WebSocket]: Connection closed');
                    attemptReconnect(connect);
                };
            };

            connect();

            return () => {
                isUnmounting.current = true;
                if (ws.current) {
                    ws.current.close();
                    ws.current = null;
                }
            };
        }
    );

    const isConnected = ws.current?.readyState === WebSocket.OPEN;

    return {
        data,
        error,
        isConnected
    };
}
